import isEqual from 'lodash/isEqual';

import {BarSelection} from 'common/charts/visualization/conversion-column-viz';
import {ConversionStepBarType, NormalConversionStep} from 'common/charts/visualization/conversion-column-viz/util';
import {InsightsBookmarkParams, InsightsQueryResults, InsightsSeriesTree} from 'common/report/insights/util/types';
import {CountType} from 'common/report/funnels/models/types';
import {OVERALL_PROP, OVERALL_PROP_NAME} from 'common/report/funnels/models/constants';
import {getBehaviorShowClauses} from 'common/report/insights/models/utils';
import {getSegmentDisplayName} from 'common/report/funnels/util';
import {getSegmentPropertyType} from 'common/query-builder/query-block/segment-menu/types';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getGroupByProperties} from 'common/query-builder/query-block/segment-menu/util';
import {Report} from 'common/report/insights/models/report';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {flattenSeriesToDepth, formatDateRange} from 'common/report/insights/util';
import {getCalendarDate} from 'common/util/date';
import {reconcileComparisonSegments} from 'common/report/funnels/util/step-formatter';
import {FUNNELS_MATH_TYPES} from 'common/report/constants';
import {renameEvent} from 'common/query-builder/cohorts-query-builder/util';

import {StepChartData} from '../util';
import {FunnelStepBookmarkType, FunnelTableRow} from '../types';
import {getColumnGroups, getRows} from '../table-util';
import {StepChartBookmarkController} from './index';

export class MultimetricStepBookmarkController implements StepChartBookmarkController {
  type = FunnelStepBookmarkType.MULTIMETRIC;
  bookmark: InsightsBookmarkParams;
  queryResults: InsightsQueryResults;

  constructor(bookmark: InsightsBookmarkParams, results: InsightsQueryResults) {
    this.bookmark = bookmark;
    this.queryResults = results;
  }

  public getBookmarkWithAppliedDefaults(bookmark: InsightsBookmarkParams) {
    return bookmark;
  }

  public getConversionSteps(): Array<NormalConversionStep<never, string>> {
    if (this.isEmptyReport()) {
      return null;
    }
    const normal = this.getBars(this.queryResults.series, ConversionStepBarType.Normal);

    if (this.queryResults.time_comparison) {
      const comparison = this.getBars(this.queryResults.time_comparison.series, ConversionStepBarType.Comparison);
      return normal.map((stepInfo, idx) => {
        const comparisonMap = new Map(comparison[idx].bars.map((bar) => [bar.extras, bar]));
        return {...stepInfo, bars: stepInfo.bars.flatMap((bar) => [bar, comparisonMap.get(bar.extras)])};
      });
    } else {
      return normal;
    }
  }
  public getBars(series: InsightsSeriesTree, type: ConversionStepBarType): Array<NormalConversionStep<never, string>> {
    const behaviors = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    const metrics = Object.values(series)[0];
    return behaviors?.map((behavior, i) => {
      const label = renameEvent(renameEvent(behavior.name));
      // TODO nix the numbered label generation for something more robust (like reading from meta field)
      const queryLabel = `${i + 1}. ${label}`;
      let bars;

      if (this.getBreakdownCount() === 0) {
        bars = [
          {
            displayName: OVERALL_PROP_NAME,
            extras: OVERALL_PROP,
            value: metrics[`count`][queryLabel][`all`],
            conversionRate: metrics[`step_conv_ratio`][queryLabel][`all`],
            type,
          },
        ];
      } else {
        const flatKeys = flattenSeriesToDepth(metrics, this.getBreakdownCount() - 1);
        bars = flatKeys.map(([keys, leaf]) => ({
          value: leaf[`count`][queryLabel][`all`],
          conversionRate: leaf[`step_conv_ratio`][queryLabel][`all`],
          displayName: this.getSegmentLabelForPath(keys),
          extras: this.getSegmentLabelForPath(keys),
          type,
        }));
      }
      return {
        displayName: label,
        bars,
      };
    });
  }
  public getSelectedStepBars(): BarSelection[] {
    // TODO
    return [{stepIndex: 0, barIndex: 0, isDropoff: false}];
  }

  private getStandardFunnelSegments() {
    const series = this.queryResults?.series;
    return this._getFunnelSegments(series);
  }

  private getLiftFunnelSegments() {
    const series = this.queryResults.lift_comparison?.series;
    return this._getFunnelSegments(series);
  }

  private getTimeComparisonFunnelSegments() {
    const series = this.queryResults.time_comparison?.series;
    return this._getFunnelSegments(series);
  }

  private _getFunnelSegments(series: InsightsSeriesTree) {
    if (!series) {
      return null;
    }
    const behaviors = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    const metrics = Object.values(series)[0];
    const breakdownCount = this.getBreakdownCount();
    if (!breakdownCount) {
      return [
        {
          prop: OVERALL_PROP,
          propName: OVERALL_PROP_NAME,
          props: [
            {
              prop: OVERALL_PROP,
              propName: OVERALL_PROP_NAME,
            },
          ],
          steps: behaviors?.map((behavior, i) => {
            const label = renameEvent(behavior.name);
            const queryLabel = `${i + 1}. ${label}`;
            return {
              count: metrics[`count`][queryLabel][`all`],
              avg_time: metrics[`avg_time`][queryLabel][`all`],
              avg_time_from_start: metrics[`avg_time_from_start`][queryLabel][`all`],
              event: behavior.name,
              goal: behavior.name,
              step_label: label,
              overall_conv_ratio: metrics[`overall_conv_ratio`][queryLabel][`all`], // TODO this is actually different but feels more correct
              step_conv_ratio: metrics[`step_conv_ratio`][queryLabel][`all`],
              index: i,
            };
          }),
        },
      ];
    } else {
      // const uniqueSegments = this.bookmark.legend?.filteredBreakdownSegments?.map((filteredSegment) => filteredSegment.selected.map((selected) => selected.key)) ?? this.getSegmentsFromResponse(metrics, groups.length);
      const uniqueSegments = flattenSeriesToDepth(metrics, breakdownCount - 1);
      // TODO color
      return uniqueSegments.map(([path, leaf]) => ({
        prop: this.getSegmentLabelForPath(path),
        propName: this.getSegmentLabelForPath(path),
        props: path.map((seg) => ({propName: seg, prop: seg})), // TODO renames?
        steps: behaviors?.map((behavior, i) => {
          const label = renameEvent(behavior.name);
          const queryLabel = `${i + 1}. ${label}`;
          return {
            count: leaf[`count`][queryLabel][`all`],
            avg_time: leaf[`avg_time`][queryLabel][`all`],
            avg_time_from_start: leaf[`avg_time_from_start`][queryLabel][`all`],
            event: behavior.name,
            goal: behavior.name,
            step_label: label,
            overall_conv_ratio: leaf[`overall_conv_ratio`][queryLabel][`all`], // TODO this is actually different but feels more correct
            step_conv_ratio: leaf[`step_conv_ratio`][queryLabel][`all`],
            index: i,
          };
        }),
      }));
    }
  }

  private getCountType(): CountType {
    const countType = getBehaviorShowClauses(this.bookmark.sections.show)[0]?.measurement.math;
    // Subset of count types for now since new metrics dont follow the same rules atm
    return countType === FUNNELS_MATH_TYPES.CONVERSION_RATE ? CountType.ConversionRate : CountType.Unique;
  }

  public getStepChartData({segmentFilterController}): StepChartData {
    // TODO
    const savedLegend = segmentFilterController.toBookmarkSavedLegend(); // TODO weird dependency, this makes the state flow kind of circular
    const formattedDateRange = formatDateRange(
      {
        fromDate: this.queryResults.date_range?.from_date,
        toDate: this.queryResults.date_range?.to_date,
      },
      {isPartialDay: false, isMultiDay: true, includeYear: true},
    );
    const formattedTimeComparisonDateRange = formatDateRange(
      {
        fromDate: this.queryResults.time_comparison?.date_range?.from_date,
        toDate: this.queryResults.time_comparison?.date_range?.to_date,
      },
      {isPartialDay: false, isMultiDay: true, includeYear: true},
    );
    const comparisonDateRange = this.queryResults.time_comparison
      ? {
          from: getCalendarDate(this.queryResults.time_comparison?.date_range?.from_date),
          to: getCalendarDate(this.queryResults.time_comparison?.date_range?.to_date),
        }
      : null;
    return {
      conversionSteps: this.getConversionSteps(),
      legendSegmentItems: this.getLegendSegmentItems(false), //SegmentItem[];
      detailedLegendSegmentItems: this.getLegendSegmentItems(true), // SegmentItem[];
      legendSegmentLabel: `legendSegmentLabel`,
      savedLegend,
      mainDateRangeLabel: formattedDateRange,
      comparisonDateRangeLabel: this.queryResults.time_comparison && formattedTimeComparisonDateRange,
      comparisonDateRange,
      countType: this.getCountType(),
      funnelSegmentsForPopover: this.getStandardFunnelSegments(), //FunnelSegment[];
      funnelSegments: this.getStandardFunnelSegments(), // FunnelSegment[];
      funnelLiftSegments: this.getLiftFunnelSegments(), // null | FunnelSegment[];
      funnelCompareSegments: this.getTimeComparisonFunnelSegments(), // FunnelSegment[] | null;
    };
  }

  public getSorting() {
    // TODO funnels specific sorting for insights bookmarks?
    return this.bookmark.sorting.bar;
  }

  getSegmentLabelForPath(segments: string[]): string {
    return segments.join(` / `);
  }

  getLegendSegmentItems(detailedLabel: boolean) {
    // TODO re-use getSegmentLabelWithConversionRate? Or add lift comparisonc
    const metrics = Object.values(this.queryResults.series)[0];
    if (this.getBreakdownCount() === 0) {
      const overall = metrics[`overall_conv_ratio`];
      const conversionRate = Object.values(overall)[0][`all`];
      return [
        {
          id: OVERALL_PROP,
          label: `${OVERALL_PROP_NAME} • ${Math.round(conversionRate * 10000) / 100}%`,
        },
      ];
    } else {
      const uniqueSegments = flattenSeriesToDepth(metrics, this.getBreakdownCount() - 1);
      return uniqueSegments
        .map(([path, leaf]) => {
          const key = this.getSegmentLabelForPath(path);
          const overallConversion = Object.values(leaf[`overall_conv_ratio`])[0][`all`];
          return {
            id: key,
            label: detailedLabel ? `${key} • ${Math.round(overallConversion * 10000) / 100}%` : key,
          };
        })
        .filter(({id}) => id !== OVERALL_PROP);
    }
  }

  getSegmentColumnSpecs(bookmark: InsightsBookmarkParams) {
    let segmentColumns = [];
    // TODO event comparison
    // TODO the rest of this
    if (bookmark.sections.group) {
      segmentColumns = segmentColumns.concat(
        bookmark.sections.group.map((segment) => {
          return {
            title: segment.value,
            //segmentType: segment.typ,
          };
        }),
      );
    }
    return segmentColumns;
  }

  getBookmarkAsReport() {
    // Utility to get the bookmark in report form (which has some serialization done on it)
    return Report.fromBookmark({params: this.bookmark, type: BookmarkType.Insights, name: ``, description: ``});
  }

  public getDataTableInfo({stepChartData, tableWidth}) {
    // the "selected" color Map assigns "tbd" (an arbitrary invalid string) for all keys because the generated table row color is expected to be overridden by SegmentFilterController in <mp-segment-filter-data-table>
    // but getRows() is an older method (still used outside this widget) that expects a color string
    const selected = stepChartData.savedLegend?.length
      ? new Map<string, string>(stepChartData.savedLegend[0].selected.map((colorChoice) => [colorChoice.key, `tbd`]))
      : new Map();
    const liftSegmentSteps = this.getLiftFunnelSegments();
    const compareSegmentSteps = this.getTimeComparisonFunnelSegments();
    const segmentSteps = this.getStandardFunnelSegments();
    const rows: FunnelTableRow[] = getRows({
      selected,
      controlGroup: null,
      shouldShowStatSig: false,
      segmentSteps,
      shouldFilterForSelected: false,
      liftComparison: this.bookmark.liftComparison,
      liftSegmentSteps,
      compareSegmentSteps,
    });
    // const spec = this.getSegmentColumnSpecs(this.bookmark)

    const selectedColumns = this.bookmark.displayOptions.funnelStepsSelectedTableColumns;
    const segments = getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses, false);
    const segmentSpec = segments.map((segment) => ({
      title: getSegmentDisplayName(segment),
      segmentType: getSegmentPropertyType(segment),
    }));
    const stepNames = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors?.map((step) =>
      renameEvent(step.name),
    );
    const segmentTypes = getGroupByProperties(segments).map((prop) => prop && prop.propertyType);
    const columnGroups = getColumnGroups({
      segmentSpec,
      onStatSigClick: () => null,
      stepNames,
      segmentTypes,
      shouldShowStatSig: false,
      selectedColumns,
      tableWidth,
      showStepNamesInGroupHeaders: true,
      liftType: this.bookmark.liftComparison?.type,
    });
    if (compareSegmentSteps && !liftSegmentSteps) {
      const comparisonRows = getRows({
        selected,
        controlGroup: null,
        shouldShowStatSig: false,
        segmentSteps: reconcileComparisonSegments({
          segments: segmentSteps,
          comparisonSegments: compareSegmentSteps,
          updatePropName: true,
        }),
        shouldFilterForSelected: false, // TODO download mode
      });
      return {rows, comparisonRows, columnGroups};
    }
    // TODO
    return {rows, columnGroups};
  }

  public getDataGroupId(): string {
    return this.bookmark.sections.globalDataGroupId;
  }

  public isEmptyReport(): boolean {
    // when swapping reports there is a brief period where the response is for the wrong chart
    // TOOD is there a better way to detect that or just not allow that?
    return !this.queryResults.series || Object.values(this.queryResults.series).length === 0;
  }

  public isEquivalent(bookmark: any, results: any) {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }

  private getBreakdownCount() {
    return this.bookmark.sections.group.length;
  }
}
